import React, { useEffect, useMemo, useState } from "react";
import { MODES_OPTIONS } from "../../types/generics.types";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { CreatePageList } from "../../components/Layouts/PageList";
import { Box, Grid } from "@mui/material";
import AnalysisTritioForm from "./components/analysis/analysisTritioForm";
import { MAutocomplete, MButton } from "@app/components";
import AnalysisFe55Form from "./components/analysis/analysisFe55Form";
import {
  useAnalysisTypes,
  useEfficiencies,
  useEquipmentMide,
} from "@services/servicesGenericsTables";
import useAuth from "@app/context/AuthContext";
import { getDefaultValues, mapCalibrationTritium } from "./utils/helpers";
import { useCalibrationTritium } from "./utils/useCalibration";
import { errorAlert } from "@app/utils/alerts";
import AnalysisAlfaTotalForm from "./components/analysis/analysisAlfaTotalForm";
import AnalysisBetaTotalPPForm from "./components/analysis/analysisBetaTotalPPForm";
import { KeysTabsAnalysis } from "@app/types/samples.types";
import AnalysisBetaForm from "./components/analysis/analysisBetaForm";
import AnalysisSpectrometryAlfa from "./components/analysis/analysisSpectrometryAlfa";
import { usecalibrationPatternSource } from "./utils/usecalibrationPatternSource";
import { useAdjustmentCurve } from "./utils/useAdjustmentCurve";
import { CurvaTypes } from "@app/types/report.types";
import MessageDialog from "@app/components/Login/MessageDialog";
import { normalizeDate } from "@app/utils/functions";

interface EditCalibrationsProps {
  mode: MODES_OPTIONS;
}

interface FormComponentProps {
  mode: MODES_OPTIONS;
}

const ComponentForm: {
  [key: string]: React.ComponentType<FormComponentProps>;
} = {
  [KeysTabsAnalysis.H3_A]: AnalysisTritioForm,
  [KeysTabsAnalysis.Fe55]: AnalysisFe55Form,
  [KeysTabsAnalysis.Ni63]: AnalysisFe55Form,
  [KeysTabsAnalysis.AT]: AnalysisAlfaTotalForm,
  [KeysTabsAnalysis.H3_V]: AnalysisTritioForm,
  [KeysTabsAnalysis.BPP]: AnalysisBetaTotalPPForm,
  [KeysTabsAnalysis.BT]: AnalysisBetaForm,
  [KeysTabsAnalysis.BR]: AnalysisBetaForm,
  [KeysTabsAnalysis.Pu238]: AnalysisSpectrometryAlfa,
  [KeysTabsAnalysis.Pu239_240]: AnalysisSpectrometryAlfa,
  [KeysTabsAnalysis.AM241]: AnalysisSpectrometryAlfa,
  [KeysTabsAnalysis.Cm243_244]: AnalysisSpectrometryAlfa,
};

export const isSpectrometryAnalysis = [
  KeysTabsAnalysis.Pu238,
  KeysTabsAnalysis.Pu239_240,
  KeysTabsAnalysis.AM241,
  KeysTabsAnalysis.Cm243_244,
];

export const hasRowCurva: Partial<KeysTabsAnalysis>[] = [
  KeysTabsAnalysis.H3_A,
  KeysTabsAnalysis.Fe55,
  KeysTabsAnalysis.Ni63,
  KeysTabsAnalysis.AT,
  KeysTabsAnalysis.H3_V,
  KeysTabsAnalysis.BT,
  KeysTabsAnalysis.BR, // Beta Resto también usa curva de ajuste
];

const EditCalibration = ({ mode }: EditCalibrationsProps) => {
  const navigate = useNavigate();
  const { idCalibration } = useParams();
  const isView = mode === MODES_OPTIONS.VIEW;
  const idEdit = mode === MODES_OPTIONS.EDIT;
  const { fetchByAnalysisType } = useEquipmentMide();
  const { analysisTypeList } = useAnalysisTypes();
  const { save } = usecalibrationPatternSource();
  const { saveAdjustmentCurves } = useAdjustmentCurve();
  const deleteCodes = [KeysTabsAnalysis.I131];
  const {
    saveCalibrationTritium,
    fechtCalibrationTritium,
    calibrationTritium,
  } = useCalibrationTritium();
  const { user } = useAuth();
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [dataToSave, setDataToSave] = useState<any | null>(null);
  const { efficienciesList } = useEfficiencies();

  const filteredAnalysisType = analysisTypeList
    .filter((item) => !deleteCodes.includes(item.codigo as KeysTabsAnalysis)) // delete analysis
    .map((item) => ({
      ...item,
      descripcion:
        item.codigo === KeysTabsAnalysis.BT
          ? "Beta Total"
          : item.codigo === KeysTabsAnalysis.BR
          ? "Beta Resto"
          : item.descripcion,
    }))
    .sort((a, b) => 
      a.descripcion.localeCompare(b.descripcion, 'es', { sensitivity: 'base' })
    );

  const form = useForm({
    mode: "all",
  });

  const typeAnalysis = useWatch({
    control: form.control,
    name: "typeAnalysis",
  });

  const equipment = useWatch({
    control: form.control,
    name: "equipment",
  });

  const date = useWatch({
    control: form.control,
    name: "date",
  });

  const codeAnalysis: KeysTabsAnalysis = Array.isArray(typeAnalysis)
    ? (typeAnalysis[0]?.codigo as KeysTabsAnalysis)
    : (typeAnalysis?.codigo as KeysTabsAnalysis);

  const matchedEfficiencies = useMemo(() => {
    return efficienciesList.filter(
      (efficiencie) => {
        // Manejar typeAnalysis como array o objeto individual
        const typeAnalysisId = Array.isArray(typeAnalysis)
          ? typeAnalysis[0]?.id
          : typeAnalysis?.id;

        // Manejar efficiency.analysis como array o objeto individual  
        const efficiencyAnalysisId = Array.isArray(efficiencie.analysis)
          ? efficiencie.analysis[0]?.id
          : (efficiencie.analysis as any)?.id;

        return efficiencyAnalysisId === typeAnalysisId &&
               efficiencie.equipment?.id === equipment?.id &&
               normalizeDate(new Date(efficiencie.date)).toUTCString() ===
                 normalizeDate(new Date(date)).toUTCString();
      }
    );
  }, [efficienciesList, typeAnalysis, equipment, date]);

  useEffect(() => {
    if (idCalibration) fechtCalibrationTritium(Number(idCalibration));
  }, [idCalibration]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (calibrationTritium) {
      const resetData = async () => {
        const values = await getDefaultValues(calibrationTritium, user);
        // Asegurarse de que typeAnalysis sea un array para compatibilidad con multiple=true
        if (values.typeAnalysis && !Array.isArray(values.typeAnalysis)) {
          (values as any).typeAnalysis = [values.typeAnalysis];
        }
        form.reset(values);
      };
      resetData();
    }
  }, [calibrationTritium]); // eslint-disable-line react-hooks/exhaustive-deps

  // Controlar la selección múltiple de análisis
  useEffect(() => {
    if (Array.isArray(typeAnalysis) && typeAnalysis.length > 1) {
      const tritiumCodes = [KeysTabsAnalysis.H3_A, KeysTabsAnalysis.H3_V];
      const betaCodes = [KeysTabsAnalysis.BT, KeysTabsAnalysis.BR];
      const spectrometryAlfaCodes = [
        KeysTabsAnalysis.Pu238, 
        KeysTabsAnalysis.Pu239_240, 
        KeysTabsAnalysis.AM241, 
        KeysTabsAnalysis.Cm243_244
      ];
      const selectedCodes = typeAnalysis.map(t => t.codigo);
      
      // Normalizar códigos para comparación (convertir a mayúsculas para evitar problemas de case-sensitivity)
      const normalizedSelectedCodes = selectedCodes.map(code => code?.toUpperCase());
      const normalizedTritiumCodes = tritiumCodes.map(code => code.toUpperCase());
      const normalizedBetaCodes = betaCodes.map(code => code.toUpperCase());
      const normalizedSpectrometryAlfaCodes = spectrometryAlfaCodes.map(code => code.toUpperCase());
      
      // Verificar si todas las selecciones son tritio
      const allTritium = normalizedSelectedCodes.every(code => normalizedTritiumCodes.includes(code));
      
      // Verificar si todas las selecciones son beta (BT o BR)
      const allBeta = normalizedSelectedCodes.every(code => normalizedBetaCodes.includes(code));
      
      // Verificar si todas las selecciones son espectrometría alfa
      const allSpectrometryAlfa = normalizedSelectedCodes.every(code => normalizedSpectrometryAlfaCodes.includes(code));
      
      // Si hay mezclas que no son tritio, beta ni espectrometría alfa, permitir solo una
      if (!allTritium && !allBeta && !allSpectrometryAlfa) {
        // Mantener solo la última selección pero como array
        form.setValue("typeAnalysis", [typeAnalysis[typeAnalysis.length - 1]]);
        return;
      }
      
      // Si todas las selecciones son tritio, permitir hasta 2
      if (allTritium && typeAnalysis.length > 2) {
        form.setValue("typeAnalysis", typeAnalysis.slice(0, 2));
      }
      
      // Si todas las selecciones son beta, permitir hasta 2
      if (allBeta && typeAnalysis.length > 2) {
        form.setValue("typeAnalysis", typeAnalysis.slice(0, 2));
      }
      
      // Si todas las selecciones son espectrometría alfa, permitir hasta 4
      if (allSpectrometryAlfa && typeAnalysis.length > 4) {
        form.setValue("typeAnalysis", typeAnalysis.slice(0, 4));
      }
    }
  }, [typeAnalysis, form]);

  useEffect(() => {
    const fetchData = async () => {
      const analysisId = Array.isArray(typeAnalysis)
        ? typeAnalysis[0]?.id
        : typeAnalysis?.id;
      if (analysisId) {
        await fetchByAnalysisType(analysisId as number);
      }
    };
    if (typeAnalysis && (Array.isArray(typeAnalysis) ? typeAnalysis.length > 0 : typeAnalysis)) {
      fetchData();
    }
  }, [typeAnalysis]); // eslint-disable-line react-hooks/exhaustive-deps

  // Obtener el código del primer tipo de análisis y normalizarlo para buscar el formulario correcto
  const getFormCode = () => {
    if (!typeAnalysis) return null;
    
    const firstCode = Array.isArray(typeAnalysis) ? typeAnalysis[0]?.codigo : typeAnalysis?.codigo;
    if (!firstCode) return null;
    
    // Buscar en ComponentForm usando el código exacto
    if (ComponentForm[firstCode]) {
      return firstCode;
    }
    
    // Si no se encuentra, buscar por coincidencia case-insensitive
    const upperCode = firstCode.toUpperCase();
    const matchingKey = Object.keys(ComponentForm).find(key => key.toUpperCase() === upperCode);
    
    return matchingKey || firstCode;
  };

  const SelectedForm = typeAnalysis ? ComponentForm[getFormCode()] : null;

  const submit = async (data: any) => {
    // Guardar CalibrationPatternSource antes de mapear
    const calibrationPatternSource = data.CalibrationPatternSource;
    
    const outputObj = mapCalibrationTritium(data);
    const {
      rowsCurveAbs,
      rowsCurveAbsK40,
      rowsCurvaPreload,
      rowsCurvaK40Preload,
      ...rest
    } = outputObj;
    const status = await saveCalibrationTritium(
      rest,
      Number(idCalibration) || undefined
    );

    if (status.success) {
      // Comparación case-insensitive para tipos de espectrometría alfa
      const isSpectrometry = isSpectrometryAnalysis.some(
        code => code.toUpperCase() === codeAnalysis?.toUpperCase()
      );
      
      if (isSpectrometry) {
        await save(calibrationPatternSource, status.data.id);
        navigate(-1);
      } else if (rowsCurveAbs) {
        await saveAdjustmentCurves(
          rowsCurveAbs,
          status.data.id,
          rowsCurvaPreload as CurvaTypes[]
        );
        if (rowsCurveAbsK40) {
          await saveAdjustmentCurves(
            rowsCurveAbsK40,
            status.data.id,
            rowsCurvaK40Preload as CurvaTypes[]
          );
        }
        navigate(-1);
      } else navigate(-1);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleSave = (data: any) => {
    // Only open modal for Tritio water for now
    const firstAnalysisCode = Array.isArray(typeAnalysis) 
      ? typeAnalysis[0]?.codigo 
      : typeAnalysis?.codigo;
      
    if (
      firstAnalysisCode === KeysTabsAnalysis.H3_A &&
      matchedEfficiencies.length <= 0
    ) {
      setDataToSave(data);
      setOpenConfirmModal(true);
    } else {
      submit(data);
    }
  };

  const handleDialogClose = () => setOpenConfirmModal(false);

  const handleDialogConfirm = () => {
    if (dataToSave) {
      submit(dataToSave);
    }
    handleDialogClose();
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(handleSave)}>
        <CreatePageList
          title={
            isView
              ? `Ver calibración de análisis - C${idCalibration}`
              : idCalibration
              ? `Editar calibración de análisis - C${idCalibration}`
              : `Nueva calibración de análisis`
          }
          footer={
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                width: "100%",
              }}
            >
              {!isView && SelectedForm && (
                <MButton
                  variant="contained"
                  color="primary"
                  text="Guardar calibración"
                  type="submit"
                />
              )}
              <MButton
                variant="outlined"
                color="secondary"
                sx={{ ml: 2 }}
                text="Cancelar"
                onClick={handleBack}
              />
            </Box>
          }
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={2} mt={2}>
              <MAutocomplete
                key={JSON.stringify(form.getValues("typeAnalysis"))}
                name="typeAnalysis"
                options={filteredAnalysisType}
                label="Tipo de análisis"
                getOptionLabel={(option) => option.descripcion}
                validation={{ required: "El tipo de análisis es requerido" }}
                disabled={isView || idEdit}
                multiple
              />
            </Grid>
          </Grid>

          {SelectedForm && (
            <SelectedForm
              mode={mode}
              {...(codeAnalysis && { 
                typeAnalysis: Array.isArray(typeAnalysis) 
                  ? typeAnalysis[0]?.codigo 
                  : typeAnalysis?.codigo 
              })}
            />
          )}
        </CreatePageList>
      </form>
      <MessageDialog
        data={{
          component: "Confirm",
          title:
            "No se ha encontrado ninguna eficiencia con la misma fecha de calibración de análisis introducida",
          message: "¿Desea guardar igualmente?",
          status: "confirm",
          active: openConfirmModal,
          handleConfirm: handleDialogConfirm,
        }}
        handleClose={handleDialogClose}
        handleConfirm={handleDialogConfirm}
      ></MessageDialog>
    </FormProvider>
  );
};

export default EditCalibration;
